/**
 * Emotional Offset Orchestration — Phase 1..4
 *
 * Selective, psychologically-tuned application of the GlobalTvOffsetBus.
 * Instead of blindly delaying every signal by the user's TV offset, each
 * emotional signal type gets its OWN multiplier so we keep:
 *   - spoiler safety on goals (full offset)
 *   - predictive edge on confidence/momentum (near-realtime)
 *   - viral pacing on cards / narrative (light/medium delay)
 *
 * Pure deterministic helpers + 1 React hook. No polling, no AI, no I/O.
 */
import { useMemo } from "react";
import {
  useGlobalTvOffset,
  useDelayedSignal,
} from "@/lib/sync/globalTvOffsetBus";

/* ---------------- Signal taxonomy ---------------- */

export type EmotionalSignalKind =
  | "goal_toast"
  | "goal_alert"
  | "heat_window"
  | "narrative_beat"
  | "viral_card"
  | "momentum"
  | "confidence"
  | "contrarian"
  | "crowd"
  | "radar_alert"
  | "poll"
  | "story_arc";

/**
 * Base multipliers (0..1) applied to the global TV offset for each signal.
 * 1.0 = full offset (spoiler-safe). 0.0 = realtime (predictive edge).
 */
export const BASE_OFFSET_WEIGHTS: Record<EmotionalSignalKind, number> = {
  goal_toast: 1.0,
  goal_alert: 1.0,
  radar_alert: 0.85,
  heat_window: 0.55,
  narrative_beat: 0.5,
  story_arc: 0.5,
  viral_card: 0.3,
  poll: 0.4,
  contrarian: 0.45,
  crowd: 0.35,
  momentum: 0.15,
  confidence: 0.05,
};

/* ---------------- Temporal experience modes ---------------- */

export type TemporalMode = "ultra_live" | "balanced" | "tv_sync" | "predictive";

export interface TemporalModeProfile {
  /** Global multiplier on top of per-signal weights. */
  intensity: number;
  /** How aggressive predictive overlays may be (0..1). */
  predictionAggressiveness: number;
  /** Allow spoiler-prone signals to flash early. */
  allowSpoilers: boolean;
  /** Pacing factor for narrative/heat (1 = normal, >1 slower). */
  emotionalPacing: number;
  label: string;
  detail: string;
}

export const TEMPORAL_MODES: Record<TemporalMode, TemporalModeProfile> = {
  ultra_live: {
    intensity: 0.0,
    predictionAggressiveness: 1.0,
    allowSpoilers: true,
    emotionalPacing: 0.9,
    label: "Ultra Live",
    detail: "Máxima ventaja predictiva. Sin retraso emocional.",
  },
  balanced: {
    intensity: 0.65,
    predictionAggressiveness: 0.75,
    allowSpoilers: false,
    emotionalPacing: 1.0,
    label: "Balanced",
    detail: "Sincronización parcial con TV, edge predictivo intacto.",
  },
  tv_sync: {
    intensity: 1.0,
    predictionAggressiveness: 0.5,
    allowSpoilers: false,
    emotionalPacing: 1.15,
    label: "TV Sync",
    detail: "Spoiler-safe total. Alineado a tu TV.",
  },
  predictive: {
    intensity: 0.25,
    predictionAggressiveness: 1.0,
    allowSpoilers: true,
    emotionalPacing: 0.85,
    label: "Predictive",
    detail: "Máximo hype anticipado. Para fans, no spoiler-averse.",
  },
};

const MODE_STORAGE_KEY = "vs:temporal_mode";

export function getTemporalMode(): TemporalMode {
  if (typeof window === "undefined") return "balanced";
  const v = window.localStorage.getItem(MODE_STORAGE_KEY) as TemporalMode | null;
  return v && v in TEMPORAL_MODES ? v : "balanced";
}

export function setTemporalMode(mode: TemporalMode): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  window.dispatchEvent(new CustomEvent("vs:temporal_mode_changed", { detail: mode }));
}

/* ---------------- Adaptive Offset Intensity ---------------- */

export interface AdaptiveOffsetContext {
  /** Raw user offset in seconds (signed). */
  rawOffsetSec: number;
  /** Active temporal mode. */
  mode: TemporalMode;
  /** Optional historical delay drift (0..1). 0 = stable, 1 = chaotic. */
  delayDriftScore?: number;
  /** Optional: user has manually tuned offset in this session. */
  manuallyTuned?: boolean;
}

/**
 * Compute the EFFECTIVE delay in seconds for a given emotional signal.
 * Combines base weight × mode intensity × adaptive factors.
 */
export function computeEffectiveDelay(
  kind: EmotionalSignalKind,
  ctx: AdaptiveOffsetContext,
): number {
  const base = BASE_OFFSET_WEIGHTS[kind] ?? 0.5;
  const profile = TEMPORAL_MODES[ctx.mode];
  // Only positive offsets matter (we can't show the future).
  const rawPositive = Math.max(0, ctx.rawOffsetSec);

  // Drift dampens delay — if the user's offset is unstable, soften it.
  const driftDamp = 1 - 0.4 * Math.max(0, Math.min(1, ctx.delayDriftScore ?? 0));

  // Manual tuning is a strong signal: trust user, slight intensity boost.
  const trustBoost = ctx.manuallyTuned ? 1.1 : 1.0;

  const effective =
    rawPositive * base * profile.intensity * driftDamp * trustBoost;
  return Math.max(0, Math.round(effective * 10) / 10);
}

/* ---------------- Coherence (Phase 4) ---------------- */

/**
 * Coordinate timings across related signals so a heat window never lands
 * AFTER its corresponding goal toast, narrative beat never precedes the
 * radar alert it describes, etc. Returns delays nudged into a coherent
 * causal order while preserving each signal's character.
 */
export function coordinateCoherentTimings(
  ctx: AdaptiveOffsetContext,
  kinds: EmotionalSignalKind[],
): Record<EmotionalSignalKind, number> {
  const map: Partial<Record<EmotionalSignalKind, number>> = {};
  for (const k of kinds) map[k] = computeEffectiveDelay(k, ctx);

  // Causal floors: each key must be >= each of its predecessors.
  const order: Array<[EmotionalSignalKind, EmotionalSignalKind]> = [
    ["confidence", "momentum"],
    ["momentum", "viral_card"],
    ["viral_card", "narrative_beat"],
    ["narrative_beat", "story_arc"],
    ["story_arc", "heat_window"],
    ["heat_window", "radar_alert"],
    ["radar_alert", "goal_alert"],
    ["goal_alert", "goal_toast"],
  ];
  for (const [predecessor, successor] of order) {
    const a = map[predecessor];
    const b = map[successor];
    if (a != null && b != null && b < a) map[successor] = a;
  }
  return map as Record<EmotionalSignalKind, number>;
}

/* ---------------- React surface ---------------- */

export function useTemporalMode(): {
  mode: TemporalMode;
  profile: TemporalModeProfile;
  setMode: (m: TemporalMode) => void;
} {
  // Read-only snapshot per render is fine; mode changes are rare.
  const mode = getTemporalMode();
  return { mode, profile: TEMPORAL_MODES[mode], setMode: setTemporalMode };
}

/**
 * Hook: delay any reactive value by the EFFECTIVE delay computed for its
 * signal kind. Drop-in replacement for `useDelayedBySync` when you know
 * the signal's emotional role.
 */
export function useDelayedBySignal<T>(value: T, kind: EmotionalSignalKind): T {
  const { offsetSec } = useGlobalTvOffset();
  const { mode } = useTemporalMode();
  const sec = useMemo(
    () => computeEffectiveDelay(kind, { rawOffsetSec: offsetSec, mode }),
    [kind, offsetSec, mode],
  );
  return useDelayedSignal(value, sec);
}

/**
 * Hook: returns a frozen snapshot of effective delays for all signal
 * kinds — useful for the War Room panel and diagnostic overlays.
 */
export function useOffsetOrchestrationSnapshot(): {
  mode: TemporalMode;
  profile: TemporalModeProfile;
  rawOffsetSec: number;
  delays: Record<EmotionalSignalKind, number>;
} {
  const { offsetSec } = useGlobalTvOffset();
  const { mode, profile } = useTemporalMode();
  const delays = useMemo(
    () =>
      coordinateCoherentTimings({ rawOffsetSec: offsetSec, mode }, [
        "confidence",
        "momentum",
        "viral_card",
        "narrative_beat",
        "story_arc",
        "heat_window",
        "radar_alert",
        "goal_alert",
        "goal_toast",
        "poll",
        "contrarian",
        "crowd",
      ]),
    [offsetSec, mode],
  );
  return { mode, profile, rawOffsetSec: offsetSec, delays };
}